import { useState, useEffect, Fragment } from 'react';
import { toast } from 'react-toastify';
import { useConfirm } from 'material-ui-confirm';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Chip,
} from '@mui/material';

// Icons
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// Components
import { BrowserTitle, Spinner } from '../components';

// Services
import jobOverviewTemplateServices from '../services/jobOverviewTemplateServices';

// Utils
import messages from '../static/messages';
import { button } from '../static/theme-styles';

const JobOverviewTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', category: '' });
  const [errors, setErrors] = useState({});

  const confirm = useConfirm();

  // Load templates
  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await jobOverviewTemplateServices.getAll();
      if (response.status === 200) {
        setTemplates(response.data);
      }
    } catch (error) {
      toast.error('Failed to load templates');
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  // Open dialog for create
  const handleCreate = () => {
    setEditingTemplate(null);
    setFormData({ name: '', description: '', category: '' });
    setErrors({});
    setDialogOpen(true);
  };

  // Open dialog for edit
  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description,
      category: template.category || '',
    });
    setErrors({});
    setDialogOpen(true);
  };

  // Handle delete
  const handleDelete = (template) => {
    confirm({
      title: 'Delete Template Permanently?',
      description: `Are you sure you want to delete "${template.name}"? This action cannot be undone.`,
    })
      .then(async () => {
        try {
          const response = await jobOverviewTemplateServices.remove(template.id);
          if (response.status === 200) {
            toast.success('Template deleted');
            loadTemplates();
          }
        } catch (error) {
          toast.error('Failed to delete template');
          console.log(error);
        }
      })
      .catch(() => {});
  };

  // Handle form change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: false }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = true;
    if (!formData.description.trim()) newErrors.description = true;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle save
  const handleSave = async () => {
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      if (editingTemplate) {
        // Update
        const response = await jobOverviewTemplateServices.update(editingTemplate.id, formData);
        if (response.status === 200) {
          toast.success('Template updated');
          setDialogOpen(false);
          loadTemplates();
        }
      } else {
        // Create
        const response = await jobOverviewTemplateServices.create(formData);
        if (response.status === 201) {
          toast.success('Template created');
          setDialogOpen(false);
          loadTemplates();
        }
      }
    } catch (error) {
      toast.error('Failed to save template');
      console.log(error);
    }
  };

  // Get unique categories
  const categories = [...new Set(templates.map(t => t.category).filter(Boolean))];

  if (loading) return <Spinner />;

  return (
    <Fragment>
      <BrowserTitle title="Airmaxx Pro | Job Overview Templates" />

      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => window.history.back()}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5">Job Overview Templates</Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
            sx={button('primary')}
          >
            Add Template
          </Button>
        </Box>

        {/* Templates Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="center" width={120}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography color="text.secondary">No templates found. Create your first template!</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                templates.map((template) => (
                  <TableRow key={template.id} hover>
                    <TableCell>
                      <Typography fontWeight="medium">{template.name}</Typography>
                    </TableCell>
                    <TableCell>
                      {template.category && (
                        <Chip label={template.category} size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          maxWidth: 400, 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {template.description}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEdit(template)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => handleDelete(template)}>
                          <DeleteForeverIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTemplate ? 'Edit Template' : 'Create New Template'}
        </DialogTitle>
        <DialogContent>
          <TextField
            name="name"
            label="Template Name"
            value={formData.name}
            onChange={handleChange}
            fullWidth
            required
            error={errors.name}
            helperText={errors.name && 'Name is required'}
            sx={{ mt: 2, mb: 2 }}
          />
          <TextField
            name="category"
            label="Category"
            value={formData.category}
            onChange={handleChange}
            fullWidth
            sx={{ mb: 2 }}
            placeholder="e.g., HVAC Installation, Maintenance"
            helperText="Optional. Used to group templates in the selection dropdown."
          />
          <TextField
            name="description"
            label="Template Text"
            value={formData.description}
            onChange={handleChange}
            fullWidth
            required
            multiline
            rows={10}
            error={errors.description}
            helperText={errors.description ? 'Template text is required' : 'This text will be inserted into the Job Overview field'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editingTemplate ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default JobOverviewTemplates;

